import { resolveModuleTypeIcon } from "@frontend/pages/case-workspace/components/module-type-icon"
import type {
  AnnotationTimeSchema,
  ImageLabelSchema,
  LabelRasterSchema,
  ParsedModuleSchema,
  ScoreSchema,
  Submission,
  TextSchema,
  VectorGroup,
  VectorLabel,
  VectorSchema,
  VoiceNoteSchema
} from "@frontend/pages/case-workspace/pathology/types"
import type { AnnotationStyle, Annotation as WsiViewerAnnotation } from "@virdx/wsi-viewer"
import type React from "react"

export function parseModuleSchema(raw: string): ParsedModuleSchema {
  try {
    const obj = JSON.parse(raw) as { type?: string }
    if (obj?.type === "score") return obj as ScoreSchema
    if (obj?.type === "vector") return obj as VectorSchema
    if (obj?.type === "image_label") return obj as ImageLabelSchema
    if (obj?.type === "text") return obj as TextSchema
    if (obj?.type === "annotation_time") return obj as AnnotationTimeSchema
    if (obj?.type === "voice_note") return obj as VoiceNoteSchema
    if (obj?.type === "label_raster") return obj as LabelRasterSchema
    return null
  } catch {
    return null
  }
}

export function moduleTypeIcon(type: string): React.ReactElement {
  return resolveModuleTypeIcon(type)
}

export function sortCandidateSubmissions(items: Submission[]): Submission[] {
  const rank = (status: string) => {
    if (status === "in_progress") return 0
    if (status === "revision_needed") return 1
    return 2
  }

  return [...items].sort((a, b) => {
    const r = rank(a.status) - rank(b.status)
    if (r !== 0) return r
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })
}

function normalizeHex(input: string): string {
  return input.trim().toLowerCase()
}

export function buildVectorStyle(color: string): AnnotationStyle {
  return {
    strokeColor: color,
    strokeOpacity: 1,
    fillColor: color,
    fillOpacity: 0.2,
    strokeWidth: 2
  }
}

export function resolveVectorGroups(schema: VectorSchema): VectorGroup[] {
  if (schema.groups && schema.groups.length > 0) return schema.groups
  return [
    {
      key: "default",
      name: "Default",
      labels: schema.labels ?? []
    }
  ]
}

export function flattenVectorLabels(groups: VectorGroup[]): VectorLabel[] {
  return groups.flatMap((group) => group.labels)
}

export function findGroupForLabel(groups: VectorGroup[], labelKey: string): VectorGroup | null {
  return groups.find((group) => group.labels.some((l) => l.key === labelKey)) ?? null
}

export function resolveVectorLabelKey(
  annotation: WsiViewerAnnotation,
  labels: VectorLabel[],
  fallback: string | null
): string | null {
  if (annotation.label) {
    const byLabel = labels.find((item) => item.key === annotation.label || item.label === annotation.label)
    if (byLabel) return byLabel.key
  }

  const byColor = labels.find((item) => normalizeHex(item.color) === normalizeHex(annotation.style.strokeColor))
  if (byColor) return byColor.key

  return fallback
}
